import { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Typography,
} from '@mui/material';
import { Link, ContentCopy } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { flowsService, FlowTrackingUrls } from '../services/flows.service';
import TooltipIconButton from './TooltipIconButton';

/** Row action: opens a flow's campaign/postback (and per-path click-through) URLs.
 *  Self-contained so the flows list and the traffic-source detail grid share one dialog. */
export default function FlowTrackingUrlsAction({ id }: { id: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [dialog, setDialog] = useState<FlowTrackingUrls | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string>('');

  const open = async () => {
    try {
      const urls = await flowsService.getTrackingUrls(id);
      setDialog(urls);
      setSelectedDomain(urls.available_domains?.[0] || Object.keys(urls.domain_urls)[0] || '');
    } catch {
      enqueueSnackbar('Could not load tracking URLs', { variant: 'error' });
    }
  };
  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Copied', { variant: 'success', autoHideDuration: 1500 });
  };

  return (
    <>
      <TooltipIconButton size="small" title="Tracking URLs" onClick={open}>
        <Link sx={{ fontSize: 16 }} />
      </TooltipIconButton>
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Tracking URLs</DialogTitle>
        <DialogContent sx={{ '&.MuiDialogContent-root': { pt: 2.5 } }}>
          {dialog?.available_domains && dialog.available_domains.length > 1 && (
            <TextField
              select label="Domain" value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              fullWidth sx={{ mb: 2 }} size="small"
            >
              {dialog.available_domains.map((domain) => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </TextField>
          )}
          {(() => {
            const urls = dialog?.domain_urls[selectedDomain];
            if (!urls) return null;
            return (
              <>
                <TextField
                  label="Campaign URL (use in ads)" fullWidth value={urls.campaign_url} sx={{ mb: 2 }}
                  slotProps={{ input: { readOnly: true, endAdornment: (
                    <IconButton onClick={() => copy(urls.campaign_url)}><ContentCopy /></IconButton>
                  )}}}
                />
                {(() => {
                  const clickUrls = (urls.paths || [])
                    .filter((p) => p.click_through_urls?.length)
                    .flatMap((p) => p.click_through_urls!);
                  if (clickUrls.length === 0) return null;
                  return (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Click-through URLs (for landing page CTAs)
                      </Typography>
                      {clickUrls.map((o) => (
                        <TextField key={o.offer_id + o.position}
                          label={`#${o.position} ${o.offer_name}`} fullWidth value={o.url} sx={{ mb: 1 }} size="small"
                          slotProps={{ input: { readOnly: true, endAdornment: (
                            <IconButton size="small" onClick={() => copy(o.url)}><ContentCopy fontSize="small" /></IconButton>
                          )}}}
                        />
                      ))}
                    </>
                  );
                })()}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
